/**
 * HIVA WebChat Widget Entry Point
 * This file initializes the widget and exposes it globally
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import { ChatWidget } from './components/ChatWidget';
import type { WidgetConfig } from './types';
import { widgetBridge } from './services/widgetBridge';
import './styles/index.css';

// Global interface for the widget
interface HIVAWebChatGlobal {
  (command: 'init', config: WidgetConfig): void;
  (command: 'open'): void;
  (command: 'close'): void;
  (command: 'destroy'): void;
  (command: 'sendMessage', message: string): void;
  q?: any[];
}

// Widget instance
let widgetRoot: ReactDOM.Root | null = null;
let widgetContainer: HTMLDivElement | null = null;

// Initialize the widget
function initWidget(config: WidgetConfig) {
  if (widgetRoot) {
    console.warn('[HIVA WebChat] Widget already initialized');
    return;
  }

  // Create container
  widgetContainer = document.createElement('div');
  widgetContainer.id = 'hiva-webchat-container';
  document.body.appendChild(widgetContainer);

  // Render widget
  widgetRoot = ReactDOM.createRoot(widgetContainer);
  widgetRoot.render(
    <React.StrictMode>
      <ChatWidget config={config} />
    </React.StrictMode>
  );

  console.log('[HIVA WebChat] Widget initialized');
}

// Destroy the widget
function destroyWidget() {
  if (widgetRoot && widgetContainer) {
    widgetRoot.unmount();
    document.body.removeChild(widgetContainer);
    widgetRoot = null;
    widgetContainer = null;
    console.log('[HIVA WebChat] Widget destroyed');
  }
}

// Global API
const HIVAWebChat: HIVAWebChatGlobal = (command: string, ...args: any[]) => {
  switch (command) {
    case 'init':
      initWidget(args[0] as WidgetConfig);
      break;
    case 'open':
      if (!widgetBridge.emit('open')) {
        console.warn('[HIVA WebChat] Widget not ready. Call hiva("init") first.');
      }
      break;
    case 'close':
      if (!widgetBridge.emit('close')) {
        console.warn('[HIVA WebChat] Widget not ready. Call hiva("init") first.');
      }
      break;
    case 'sendMessage':
      if (args[0] && typeof args[0] === 'string') {
        if (!widgetBridge.emit('sendMessage', args[0])) {
          console.warn('[HIVA WebChat] Widget not ready. Call hiva("init") first.');
        }
      } else {
        console.warn('[HIVA WebChat] sendMessage requires a string argument.');
      }
      break;
    case 'destroy':
      destroyWidget();
      break;
    default:
      console.warn(`[HIVA WebChat] Unknown command: ${command}`);
  }
};

// Process queued commands
if (window.hiva && Array.isArray(window.hiva.q)) {
  window.hiva.q.forEach((args: any[]) => {
    if (args.length > 0) {
      const command = args[0];
      const rest = args.slice(1);
      (HIVAWebChat as any)(command, ...rest);
    }
  });
}

// Expose global API
window.hiva = HIVAWebChat as any;

// Type declarations for window
declare global {
  interface Window {
    hiva: any;
  }
}

export default HIVAWebChat;
